import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/ThemeContext";
import "../styles/NewIndexPage.css";
import "../styles/IndexHeaderNav.css";
import ProfileMenu from "../objects/ProfileMenu";

function IndexHeaderNav({
    user = null,
    errormessage = "",
    scrollAndHighlight,
    showReturnBtn = false,
}) {
    const navigate = useNavigate();

    const [currProfileOpen, setCurrProfileOpen] = useState(false);
    const [visibleMsg, setVisibleMsg] = useState("");
    const [popupFading, setPopupFading] = useState(false);
    const fadeOutTimerRef = useRef(null);

    useEffect(() => {
        if (errormessage) {
            clearTimeout(fadeOutTimerRef.current);
            setPopupFading(false);
            setVisibleMsg(errormessage);
        } else if (visibleMsg) {
            setPopupFading(true);
            fadeOutTimerRef.current = setTimeout(() => {
                setVisibleMsg("");
                setPopupFading(false);
            }, 350);
        }
        return () => clearTimeout(fadeOutTimerRef.current);
    }, [errormessage]);

    const currProfileRef = useRef(currProfileOpen);

    const { theme } = useTheme();

    const isRootDomain = window.location.hostname.split(".").length === 2;

    // If no user prop is passed, behave like "not logged in" (show login buttons, no spinner).
    const effectiveUser = user ?? { na: true };

    const handleSearchClick = () => {
        if (typeof scrollAndHighlight === "function") {
            scrollAndHighlight("cta-left", 400);
        } else {
            navigate("/");
        }
    };


    useEffect(() => {
        currProfileRef.current = currProfileOpen;
    }, [currProfileOpen]);

    // Only show the "loading" spinner when we actually received a user object from parent
    // and it's currently in a "loading" state (not na, not error, not logged in).
    const shouldShowUserSpinner =
        user !== null &&
        !effectiveUser?.error &&
        !effectiveUser?.na &&
        !effectiveUser?.id;

    const showLoggedInUI = !!effectiveUser?.id;
    const showLoggedOutUI = !showLoggedInUI && (effectiveUser?.na || user === null);

    return (
        <div className="header-banner">
            <div className="header-left" onClick={() => navigate("/")}>
                <img src={theme.logo} alt="Rezify Logo" className="header-logo" />
                <span className="header-title">Rezify</span>
            </div>

            <div className="header-center">
                <button
                    onClick={handleSearchClick}
                    className="header-link"
                    type="button"
                >
                    Search
                </button>


                <a href="/#features-header" className="header-link">
                    Features & Filters
                </a>

                {isRootDomain && (
                    <a href="/#pricing-header" className="header-link">
                        Pricing
                    </a>
                )}

                <a href="/#about-content" className="header-link">
                    About Us
                </a>

                <a href="/#index-footer" className="header-link">
                    Contact
                </a>

                <a href="/help" className="header-link">
                    Help
                </a>
            </div>

            <div className="header-right">
                {(visibleMsg || showReturnBtn) && (
                    <div className="fail-popup-stack">
                        {visibleMsg && (
                            <div id="fail-popup" className={`fail-popup${popupFading ? " fade-out" : ""}`}>
                                {visibleMsg}
                            </div>
                        )}
                        {showReturnBtn && (
                            <button
                                className="hero-nav__btn return-results-btn"
                                type="button"
                                onClick={() => navigate("/results")}
                            >
                                Return to Results →
                            </button>
                        )}
                    </div>
                )}

                {!isRootDomain && (
                    <button
                        onClick={() => navigate("/login?type=admin")}
                        className="hero-nav__btn"
                        type="button"
                    >
                        Admin Portal
                    </button>
                )}

                {isRootDomain && showLoggedOutUI && (
                    <button
                        onClick={() => {
                            window.location.href = "/#pricing-header";
                        }}
                        className="hero-nav__btn"
                        type="button"
                    >
                        Sign Up – For Free!
                    </button>
                )}

                <a
                    href="/feedback"
                    target="_blank"
                    rel="noreferrer"
                    className="hero-nav__btn"
                >
                    Feedback
                </a>

                <div className="header-divider"></div>

                {shouldShowUserSpinner && <div className="spinner-aw"></div>}

                {showLoggedInUI && (
                    <>
                        {effectiveUser.plan === "basic" && (
                            <a
                                href={"/plans?redirect=index"}
                                className="hero-nav__btn"
                                style={{ boxShadow: "0 0px 15px var(--primary-aw)" }}
                            >
                                Upgrade
                            </a>
                        )}

                        {effectiveUser.plan === "premium" &&
                            effectiveUser.stripe_meta &&
                            effectiveUser.stripe_meta.subscription_to_be_cancelled && (
                                <a
                                    href={"/plans?redirect=index"}
                                    className="hero-nav__btn"
                                    style={{ boxShadow: "0 0px 15px var(--primary-aw)" }}
                                >
                                    Reactivate
                                </a>
                            )}

                        <div className="login-profile-section">
                            <ProfileMenu user={effectiveUser} upgradeRedirect="index" />
                        </div>
                    </>
                )}

                {showLoggedOutUI && (
                    <div className="login-profile-section">
                        <button
                            onClick={() => navigate("/login")}
                            className="hero-nav__btn"
                            type="button"
                        >
                            {!isRootDomain ? "Student Login" : "Login"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export { IndexHeaderNav };
